import React, { useRef, useState, useEffect } from "react";
import { Button } from "@app/shared";
import { getWizardState, updateUserEmail } from "../../utils/wizardStorage";
import { Sessions } from "@app/shared";
import { ForgotPasswordFormFields, isValidEmailAddress } from "../../../../shared/ForgotPasswordFormFields";

interface ForgotPasswordWizardProps {
    onSuccess: () => void;
    onBackToLoginClick: () => void;
}

export const ForgotPasswordWizard: React.FC<ForgotPasswordWizardProps> = ({ onSuccess, onBackToLoginClick }) => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState<string>();
    const [confirmation, setConfirmation] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const submitButton = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        const savedState = getWizardState();
        if (savedState.userEmail) {
            setEmail(savedState.userEmail);
        }
    }, []);

    const handleEmailChange = (newEmail: string) => {
        setEmail(newEmail);
        if (newEmail) {
            updateUserEmail(newEmail);
        }
    };

    const handleSendEmailOnClick = async () => {
        if (!isValidEmailAddress(email)) {
            setError("Please enter a valid email address.");
            return;
        }

        setIsLoading(true);
        setError(undefined);

        try {
            // Save email before sending
            updateUserEmail(email);

            await Sessions.forgotPassword(email);
            setConfirmation("Thank you. An email has been sent to the provided email address.");
            setTimeout(() => {
                onSuccess();
            }, 3000);
        } catch (e) {
            setError("Error: " + e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBackToLogin = () => {
        if (email) {
            updateUserEmail(email);
        }
        onBackToLoginClick();
    };

    return (
        <div className="w-full">
            <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Forgot Your Password?</h2>
                <p className="text-gray-600">Enter your email to receive a password reset link</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
                {confirmation ? (
                    <div className="text-center">
                        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="text-green-800">{confirmation}</p>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">Redirecting you back to sign in...</p>
                        <Button onClick={handleBackToLogin} className="w-full">
                            Back to Sign In
                        </Button>
                    </div>
                ) : (
                    <>
                        <ForgotPasswordFormFields email={email} onEmailChange={handleEmailChange} onEnter={() => submitButton.current?.click()} error={error} />

                        <div className="mt-6 space-y-3">
                            <Button ref={submitButton} onClick={handleSendEmailOnClick} className="w-full" disabled={isLoading || !email.trim()}>
                                {isLoading ? "Sending Email..." : "Send Reset Email"}
                            </Button>
                            <div className="text-center">
                                <button onClick={handleBackToLogin} className="text-blue-600 hover:text-blue-700 text-sm">
                                    Back to Sign In
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
